import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/theme";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Users,
  GraduationCap,
  HelpCircle,
  Briefcase,
  Bot,
  FileText,
  BookOpen,
  LogOut,
  Lock,
  Home,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Upload,
  LayoutDashboard,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Permission } from "@/types/common";
import { PanelSwitcher } from "@/components/PanelSwitcher";

const navigation: {
  name: string;
  href: string;
  icon: LucideIcon;
  permission?: Permission;
}[] = [
  { name: "Dashboard", href: "/dashboard", icon: Home },
  { name: "Candidates", href: "/admin/candidates", icon: Users, permission: 'candidates.read' },
  { name: "Colleges", href: "/admin/colleges", icon: GraduationCap, permission: 'colleges.read' },
  { name: "Questions", href: "/admin/questions", icon: HelpCircle, permission: 'questions.read' },
  { name: "Job Positions", href: "/admin/job-positions", icon: Briefcase, permission: 'job_positions.read' },
  { name: "Status Guide", href: "/admin/candidate-journey", icon: BookOpen, permission: 'candidates.read' },
  { name: "AI Templates", href: "/admin/ai-templates", icon: Bot, permission: 'questions.write' },
  { name: "Prompts", href: "/admin/prompts", icon: FileText, permission: 'admin.all' },
];

const lateralNavigation: { name: string; href: string; icon: LucideIcon }[] = [
  { name: "Dashboard", href: "/admin/lateral", icon: Home },
  { name: "Job Positions", href: "/admin/lateral/job-positions", icon: Briefcase },
  { name: "Candidates", href: "/admin/lateral/candidates", icon: Users },
  { name: "Bulk CV Evaluation", href: "/admin/lateral/cv-evaluation", icon: Upload },
];

const YEAR_SYNC_ROUTES = new Set([
  "/dashboard",
  "/admin/candidates",
  "/admin/colleges",
]);

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, profile, signOut } = useAuth();
  const { hasPermission } = useAuthorization();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const isLateralPanel = location.pathname.startsWith("/admin/lateral");
  const activeNav = isLateralPanel ? lateralNavigation : navigation;
  const hasLateralAccess = profile?.has_lateral_access || profile?.role === "admin";

  const isKanbanRoute = location.pathname.startsWith("/admin/lateral/kanban");
  const [kanbanOpen, setKanbanOpen] = useState(isKanbanRoute);

  const { data: lateralPositions } = useQuery({
    queryKey: ["lateral-positions-sidebar"],
    queryFn: async () => {
      const { data } = await supabase
        .from("lateral_job_positions")
        .select("id, job_title")
        .eq("is_active", true)
        .order("job_title");
      return data ?? [];
    },
    enabled: isLateralPanel && hasLateralAccess,
  });

  const getNavHrefWithYear = (href: string) => {
    if (!YEAR_SYNC_ROUTES.has(href)) {
      return href;
    }

    const currentParams = new URLSearchParams(location.search);
    const year = currentParams.get("year");
    if (!year) {
      return href;
    }

    const [pathname, queryString = ""] = href.split("?");
    const targetParams = new URLSearchParams(queryString);
    if (!targetParams.has("year")) {
      targetParams.set("year", year);
    }

    const nextQuery = targetParams.toString();
    return nextQuery ? `${pathname}?${nextQuery}` : pathname;
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/admin/login");
  };

  const handleChangePassword = () => {
    navigate("/admin/change-password");
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 flex">
      {/* Sidebar */}
      <div
        className={`
        fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-all duration-300 ease-in-out
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        ${collapsed ? "w-16" : "w-64"}
        lg:translate-x-0 lg:static lg:inset-0 shrink-0
      `}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b">
          {!collapsed && (
            <img src={theme.logo} alt="Logo" className="h-8 w-auto" />
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:inline-flex"
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <nav className="mt-6">
          <div className="px-3 space-y-1">
            {activeNav
              .filter((item) => !('permission' in item) || !item.permission || hasPermission((item as any).permission))
              .map((item) => {
                const Icon = item.icon;
                const isActive = isLateralPanel
                  ? (item.href === "/admin/lateral" ? location.pathname === item.href : location.pathname.startsWith(item.href))
                  : location.pathname === item.href;

              return (
                <Button
                  key={item.name}
                  variant={isActive ? "secondary" : "ghost"}
                  className={`w-full ${
                    isActive
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-700 hover:text-gray-900"
                  } ${
                    collapsed ? "justify-center px-2" : "justify-start px-3"
                  }`}
                  onClick={() => {
                    navigate(isLateralPanel ? item.href : getNavHrefWithYear(item.href));
                    setSidebarOpen(false);
                  }}
                  title={collapsed ? item.name : undefined}
                >
                  <Icon className={`h-5 w-5 ${collapsed ? "" : "mr-3"}`} />
                  {!collapsed && item.name}
                </Button>
              );
            })}

            {/* Kanban submenu — lateral panel only */}
            {isLateralPanel && (
              <div>
                <Button
                  variant={isKanbanRoute ? "secondary" : "ghost"}
                  className={`w-full ${
                    isKanbanRoute ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:text-gray-900"
                  } ${collapsed ? "justify-center px-2" : "justify-start px-3"}`}
                  onClick={() => {
                    if (collapsed) {
                      navigate("/admin/lateral/kanban");
                      setSidebarOpen(false);
                    } else {
                      setKanbanOpen((o) => !o);
                    }
                  }}
                  title={collapsed ? "Kanban" : undefined}
                >
                  <LayoutDashboard className={`h-5 w-5 ${collapsed ? "" : "mr-3"}`} />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left">Kanban</span>
                      {kanbanOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </>
                  )}
                </Button>

                {kanbanOpen && !collapsed && (
                  <div className="ml-4 mt-1 space-y-0.5 border-l border-gray-200 pl-3">
                    {!lateralPositions?.length && (
                      <p className="text-xs text-gray-400 py-1 px-1">No positions yet</p>
                    )}
                    {lateralPositions?.map((pos: any) => {
                      const activeKanban = location.pathname === `/admin/lateral/kanban/${pos.id}`;
                      return (
                        <button
                          key={pos.id}
                          className={`w-full text-left text-xs py-1.5 px-2 rounded-md transition-colors truncate ${
                            activeKanban
                              ? "bg-blue-50 text-blue-700 font-medium"
                              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                          }`}
                          onClick={() => {
                            navigate(`/admin/lateral/kanban/${pos.id}`);
                            setSidebarOpen(false);
                          }}
                          title={pos.job_title}
                        >
                          {pos.job_title}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </nav>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-25 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div
        className={`w-full flex-1 transition-all duration-300 ${
          collapsed ? "lg:ml-10" : "lg:ml-5"
        }`}
      >
        {/* Top navigation */}
        <header className="bg-white shadow-sm border-b">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden mr-2"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              {(profile?.has_lateral_access || profile?.role === "admin") && <PanelSwitcher />}
              <h2 className="text-lg font-medium text-gray-900">
                {activeNav.find((item) =>
                  isLateralPanel
                    ? (item.href === "/admin/lateral" ? location.pathname === item.href : location.pathname.startsWith(item.href))
                    : location.pathname === item.href
                )?.name || "Dashboard"}
              </h2>
            </div>

            <div className="flex items-center space-x-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-10 w-10 rounded-full"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarFallback>
                        {profile?.full_name?.charAt(0)?.toUpperCase() ||
                          user?.email?.charAt(0)?.toUpperCase() ||
                          "U"}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium">
                        {profile?.full_name || "User"}
                      </p>
                      <p className="w-[200px] truncate text-sm text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleChangePassword}>
                    <Lock className="mr-2 h-4 w-4" />
                    <span>Change Password</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="w-full p-6 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
